import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { Button } from "@tql/ui-components-react/tql/buttons";
import {
	checkCircleBrokenLine,
	xClose,
} from "@tql/ui-components-react/untitled/icons";
import { useState } from "react";

import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";
import { useAutoSpinner } from "~/shared/hooks/use-global-spinner.ts";
import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import type { BulkNotificationTarget } from "~/shared/schemas/shipment-notification-schema.ts";

import { useBulkTenderDocumentsEmail } from "../../hooks/use-bulk-tender-document-email.ts";
import { useBulkTenderDocuments } from "../../hooks/use-bulk-tender-documents.ts";
import type { BulkTenderDocumentsEmailResponse } from "../../schemas/bulk-tender-document-email-schema.ts";
import type { BulkTenderDocumentFormData } from "../../schemas/bulk-tender-document-schema.ts";
import type {
	BulkTenderItemResult,
	BulkTenderNotificationTarget,
	BulkTenderResponse,
} from "../../schemas/bulk-tender-schema.ts";
import { downloadBulkTenderDocumentsArchive } from "../../utils/bulk-tender-document-downloads.ts";
import { exportBulkTenderResults } from "../../utils/bulk-tender-result-export.ts";
import { BulkTenderDocuments } from "./bulk-tender-documents.tsx";
import { BulkTenderNotificationModal } from "./bulk-tender-notification-modal.tsx";
import { BulkTenderSendModal } from "./bulk-tender-send-modal.tsx";

type BulkTenderSuccessModalProps = {
	onComplete: () => void;
	onDownloadDocuments?: (formData: BulkTenderDocumentFormData) => void;
	onResultAction?: (quoteId: number, reasonCode: string) => void;
	onSendDocuments?: (formData: BulkTenderDocumentFormData) => void;
	onSubscribeToNotifications?: () => void;
	notificationTargets?: readonly BulkTenderNotificationTarget[];
	response: BulkTenderResponse;
};

export const BulkTenderSuccessModal = ({
	onComplete,
	onDownloadDocuments,
	onResultAction,
	onSendDocuments,
	onSubscribeToNotifications,
	notificationTargets = [],
	response,
}: BulkTenderSuccessModalProps) => {
	const { isInternal } = useDeploymentType();
	const { isReady: isRecipientsFlagReady, isEnabled: isRecipientsFlagEnabled } =
		useFeatureFlagGate("traxLtlPostTenderEmailRecipients");
	const { isReady: isSendFlagReady, isEnabled: isSendFlagEnabled } =
		useFeatureFlagGate("traxLtlBulkTenderShippingDocsSend");
	const isSendEnabled = isSendFlagReady && isSendFlagEnabled;
	const { showError, showSuccess, showWarning } = useNotificationActions();
	const documentMutation = useBulkTenderDocuments();
	const emailMutation = useBulkTenderDocumentsEmail();
	const [showNotificationModal, setShowNotificationModal] = useState(false);
	const [showDocuments, setShowDocuments] = useState(false);
	const [showFailedResults, setShowFailedResults] = useState(true);
	useAutoSpinner(documentMutation.isPending);
	useAutoSpinner(emailMutation.isPending);
	const [sendFormData, setSendFormData] =
		useState<BulkTenderDocumentFormData | null>(null);
	const tenderedCount = response.results.filter(
		(result) => result.status === "tendered",
	).length;
	const failedCount = response.results.length - tenderedCount;
	const failedResults = response.results.filter(
		(item): item is Extract<BulkTenderItemResult, { status: "failed" }> =>
			item.status === "failed",
	);
	const showUnavailableSubscriptionError = () => {
		showError(
			"Notification subscriptions are unavailable for these tendered shipments because customer information is missing.",
		);
	};
	const tenderedItems = response.results.filter(
		(item): item is Extract<BulkTenderItemResult, { status: "tendered" }> =>
			item.status === "tendered",
	);
	const tenderedQuoteIds = tenderedItems.map((item) => item.quoteId);
	const tenderedNotificationTargets: BulkNotificationTarget[] =
		tenderedItems.flatMap((item) => {
			const target = notificationTargets.find(
				(candidate) => candidate.quoteId === item.quoteId,
			);
			return target && item.poNumber != null
				? [{ customerId: target.customerId, poNumber: item.poNumber }]
				: [];
		});
	const canSubscribeToNotifications =
		tenderedNotificationTargets.length === tenderedCount &&
		(!isInternal ||
			tenderedNotificationTargets.every((target) => target.customerId != null));
	const showComposeScreen =
		isInternal || (isRecipientsFlagReady && isRecipientsFlagEnabled);

	const reportEmailResult = (result: BulkTenderDocumentsEmailResponse) => {
		if (result.failures.length > 0) {
			showWarning(
				`${String(result.failures.length)} document${result.failures.length === 1 ? "" : "s"} could not be generated. The available documents were emailed.`,
			);
		}
	};

	const handleDownloadDocuments = (formData: BulkTenderDocumentFormData) => {
		if (onDownloadDocuments) {
			onDownloadDocuments(formData);
			return;
		}

		documentMutation.mutate(
			{ formData, quoteIds: tenderedItems.map((item) => item.quoteId) },
			{
				onSuccess: (result) => {
					if (!result.success) {
						showError(result.error);
						return;
					}
					if (result.data.byteLength === 0) {
						showError("No bulk tender documents were available to download.");
						return;
					}
					downloadBulkTenderDocumentsArchive(result.data);
				},
				onError: () =>
					showError("Bulk tender documents could not be generated."),
			},
		);
	};
	const handleSendDocuments = (formData: BulkTenderDocumentFormData) => {
		if (!isSendEnabled) return;

		if (onSendDocuments) {
			onSendDocuments(formData);
			return;
		}

		if (showComposeScreen) {
			setSendFormData(formData);
			return;
		}

		emailMutation.mutate(
			{
				formData,
				includeTrackingLink: true,
				messageBody: undefined,
				recipients: [],
				quoteIds: tenderedQuoteIds,
				subject: undefined,
			},
			{
				onSuccess: (result) => {
					if (!result.success) {
						showError(result.error);
						return;
					}
					reportEmailResult(result.data);
					showSuccess("Bulk tender documents emailed successfully.");
				},
				onError: () => showError("Bulk tender documents could not be emailed."),
			},
		);
	};
	const documentOptions =
		tenderedCount > 0 ? (
			<BulkTenderDocuments
				isLoading={documentMutation.isPending || emailMutation.isPending}
				isSendEnabled={isSendEnabled}
				onDownload={handleDownloadDocuments}
				onSend={handleSendDocuments}
				onSubscribe={
					onSubscribeToNotifications ??
					(() => {
						if (!canSubscribeToNotifications) {
							showUnavailableSubscriptionError();
							return;
						}
						setShowNotificationModal(true);
					})
				}
			/>
		) : null;
	const notificationModal = showNotificationModal ? (
		<BulkTenderNotificationModal
			isOpen={true}
			onClose={() => setShowNotificationModal(false)}
			targets={tenderedNotificationTargets}
		/>
	) : null;

	if (sendFormData) {
		return (
			<BulkTenderSendModal
				formData={sendFormData}
				onClose={() => setSendFormData(null)}
				onSent={(result) => {
					reportEmailResult(result);
					setSendFormData(null);
				}}
				quoteIds={tenderedQuoteIds}
			/>
		);
	}

	if (showDocuments) {
		return (
			<>
				<TraxModal
					className={"bulk-tender-success-modal"}
					onCancel={onComplete}
					onClose={onComplete}
					showConfirmationButtons={false}
					title={"Get Documents"}
					type={"primary"}
					width={445}
				>
					{documentOptions}
				</TraxModal>
				{notificationModal}
			</>
		);
	}

	const handleExportResults = () => {
		exportBulkTenderResults(response.results);
	};

	return (
		<>
			<TraxModal
				cancelText={"Export Results"}
				className={"bulk-tender-modal"}
				confirmDisabled={tenderedCount === 0}
				confirmText={"Get Documents"}
				onCancel={handleExportResults}
				onClose={onComplete}
				onConfirm={() => setShowDocuments(true)}
				showConfirmationButtons={true}
				title={"Tendering Shipments"}
				type={"primary"}
				width={584}
			>
				<div className={"bulk-tender-results"}>
					<div className={"bulk-tender-results-heading"}>Tendering Results</div>
					<div className={"bulk-tender-results-summary"}>
						{tenderedCount > 0 ? (
							<div
								className={
									"bulk-tender-result-status bulk-tender-result-status-success"
								}
							>
								<SvgIcon
									className={"bulk-tender-result-status-icon"}
									icon={checkCircleBrokenLine}
									size={"large"}
								/>
								Successfully Tendered ({tenderedCount})
							</div>
						) : null}
						{failedCount > 0 ? (
							<>
								<button
									aria-controls={"bulk-tender-failure-list"}
									aria-expanded={showFailedResults}
									className={
										"bulk-tender-result-toggle bulk-tender-result-status-failure"
									}
									onClick={() => setShowFailedResults((isShown) => !isShown)}
									type={"button"}
								>
									<SvgIcon
										className={"bulk-tender-result-status-icon"}
										icon={xClose}
										size={"large"}
									/>
									Failed to Tender ({failedCount})
									<span aria-hidden={"true"}>
										{showFailedResults ? "-" : "+"}
									</span>
								</button>
								{showFailedResults ? (
									<div
										aria-label={"Failed tender details"}
										className={"bulk-tender-failure-list"}
									>
										{failedResults.map((itemResult) => (
											<div
												key={itemResult.quoteId}
												className={"bulk-tender-failure-item"}
											>
												<div className={"bulk-tender-failure-item-title"}>
													{itemResult.quoteId}
												</div>
												<span>{itemResult.reason}</span>
												{onResultAction &&
												itemResult.reasonCode === "RERATE_REQUIRED" ? (
													<Button
														onClick={() =>
															onResultAction(
																itemResult.quoteId,
																itemResult.reasonCode,
															)
														}
														variant={"secondary"}
													>
														Rerate
													</Button>
												) : null}
											</div>
										))}
									</div>
								) : null}
							</>
						) : null}
					</div>
				</div>
			</TraxModal>
			{notificationModal}
		</>
	);
};
